
/*
  Patio cart gifts: ONE free gift at a time, for the highest milestone the cart has reached.

  - The cart total counts paid items only. Gift lines carry the hidden property _patio_gift,
    so a gift never counts toward the next milestone.
  - Products in EXCLUDED_HANDLES never count toward the total, however much they cost. The rest
    of the cart still earns gifts as normal.
  - Any Shopify cart-visible discount disables ALL milestone gifts (including Rapi discounts).
  - Gifts do not stack: reaching a higher milestone swaps the current gift for the higher one,
    dropping below a milestone swaps it back down to the gift for the milestone still reached.
    Below the first milestone ($99) there is no gift.
  - The gift is kept at quantity 1.
  - If the shopper removes the gift themselves, they get no gift (not the lower one) until the
    cart drops below that milestone or reaches the next one.
  - If a gift can't be added (sold out / unpublished), the next lower gift is used instead.
  - The gift goes in BEFORE the theme draws the cart: when the theme adds or changes something,
    this waits for Shopify's reply, swaps the gift, and hands the theme a reply that already
    contains it. The drawer is drawn once, with the right gift in it. No reloads, no
    second repaint.
  - Checkout runs a fresh gift sync before proceeding on standard cart checkout buttons.

  Gifts are separate $0 products (the "... Gift" copies), so no discount is needed for them to be free.
  LIMIT: This storefront script cannot detect checkout-only discounts or enforce checkout rules.
  Rapi discounts that change prices without appearing in /cart.js may not be detected.
*/
(() => {
  if (window.PatioCartGifts) return;

  // min: cart total in dollars (paid items only).
  const GIFTS = [
    { key: 'gel-layer', min: 99, variantId: 50723516547327 }, // Cooling Gel Layer for Pillows Gift ($0)
    { key: 'organizer', min: 199, variantId: 50723518578943 }, // Bedset Storage Organizer Gift ($0), Grey
    { key: 'physio', min: 349, variantId: 50721402192127 }, // Physio Session ($0)
    { key: 'silk', min: 549, variantId: 50723520184575 }, // Silk Pillowcase (22M) Gift ($0), Light Grey
  ];

  // These products never count toward a gift milestone, whatever their price.
  const EXCLUDED_HANDLES = [
    'opium-plus',
    'opium',
    'miel',
    'patiohome-smartcool-memory-foam-mattress',
    'patiohome-lux-24',
  ];

  const PROP = '_patio_gift';
  const STORE_KEY = 'patioGifts:v2';
  const DRAWER_SECTIONS = ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
  const nativeFetch = window.fetch.bind(window);
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const failed = new Set(); // gifts the store refused on this page (sold out), not retried on this page

  /* ---------- helpers ---------- */

  const readStore = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STORE_KEY)) || { present: [], declined: [] };
    } catch (e) {
      return { present: [], declined: [] };
    }
  };
  const writeStore = (state) => {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (e) {}
  };

  const request = (url, body) =>
    nativeFetch(root + url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    }).then((res) => {
      if (!res.ok) throw res;
      return res.json();
    });

  const giftOf = (item) => (item.properties && item.properties[PROP]) || null;
  const giftByKey = (key) => GIFTS.find((gift) => gift.key === key) || null;

  // One cart write at a time, in order.
  let chain = Promise.resolve();
  let busy = 0;
  const lock = (fn) => {
    busy++;
    const next = chain.then(fn);
    chain = next.catch(() => {}).then(() => {
      busy--;
    });
    return next;
  };

  /* ---------- core: keep exactly one gift, the highest one reached ---------- */

  // Returns null when nothing changed, otherwise the fresh HTML of the requested sections.
  async function sync(sectionIds) {
    const cart = await request('cart.js');
    const store = readStore();

    const counts = (item) => !giftOf(item) && !EXCLUDED_HANDLES.includes(item.handle);
    // Detect discounts from Rapi's quantity/bundle blocks when they are represented
    // in Shopify's cart as line-level or cart-level discounts. Also blocks other
    // Shopify discounts intentionally, so customers cannot stack promotions.
    const hasDiscount =
      (Number(cart.total_discount) || 0) > 0 ||
      (Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0) ||
      cart.items.some((item) =>
        !giftOf(item) && (
          (Number(item.total_discount) || 0) > 0 ||
          (Number(item.original_line_price) || 0) > (Number(item.final_line_price) || 0) ||
          (Array.isArray(item.discounts) && item.discounts.length > 0) ||
          (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length > 0)
        )
      );

    const paidTotal = cart.items.filter(counts).reduce((sum, item) => sum + item.original_line_price, 0);
    const reached = (gift) => !hasDiscount && paidTotal > 0 && paidTotal >= Math.round(gift.min * 100);

    const giftLines = cart.items.filter((item) => giftOf(item));
    const hasLine = (key) => giftLines.some((item) => giftOf(item) === key);
    // The correct line for a gift: right key AND right variant.
    const lineFor = (gift) => giftLines.find((item) => giftOf(item) === gift.key && item.variant_id === gift.variantId) || null;

    // Forget a removal once the cart drops below that gift's milestone.
    const declined = (hasDiscount ? [] : store.declined).filter((key) => {
      const gift = giftByKey(key);
      return gift && reached(gift);
    });

    // A gift that was in the cart last time and is gone now (while still earned) was removed by the shopper.
    (hasDiscount ? [] : store.present).forEach((key) => {
      const gift = giftByKey(key);
      if (gift && reached(gift) && !hasLine(key) && !declined.includes(key)) declined.push(key);
    });

    // Gifts reached, highest milestone first, skipping ones the store refused on this page.
    const candidates = GIFTS.filter(reached)
      .sort((a, b) => b.min - a.min)
      .filter((gift) => !failed.has(gift.key));

    // The one gift this cart should hold. If the shopper removed it, no gift.
    const top = candidates[0] || null;
    const target = top && !declined.includes(top.key) ? top : null;
    const keep = target ? lineFor(target) : null;

    // Remove every gift line except the one we keep (other tiers, old bamboo gift, duplicates, wrong variants).
    const updates = {};
    giftLines.forEach((item) => {
      if (item !== keep) updates[item.key] = 0;
    });
    if (keep && keep.quantity !== 1) updates[keep.key] = 1;

    // Each write asks for the sections in the same reply, so the HTML is rendered after the change.
    const withSections = (body) =>
      sectionIds.length ? Object.assign(body, { sections: sectionIds.join(','), sections_url: window.location.pathname }) : body;

    let changed = false;
    let sections = null;
    let present = keep ? [target.key] : [];

    if (Object.keys(updates).length) {
      const res = await request('cart/update.js', withSections({ updates }));
      sections = res.sections || sections;
      changed = true;
    }

    // Add the target gift. If it can't be added, fall back to the next lower gift.
    if (target && !keep) {
      for (const gift of candidates.slice(candidates.indexOf(target))) {
        if (declined.includes(gift.key)) break;
        try {
          const res = await request(
            'cart/add.js',
            withSections({ items: [{ id: gift.variantId, quantity: 1, properties: { [PROP]: gift.key } }] })
          );
          sections = res.sections || sections;
          present = [gift.key];
          changed = true;
          break;
        } catch (e) {
          failed.add(gift.key);
          console.warn(`[patio gifts] could not add ${gift.key} (variant ${gift.variantId}). Check it is in stock and published.`);
        }
      }
    }

    writeStore({ present, declined });
    return changed ? { sections: sections || {} } : null;
  }

  /* ---------- path 1: the theme's own cart requests (the normal case) ---------- */

  const isCartWrite = (url) => /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(url));

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    if (!isCartWrite(url)) return nativeFetch(input, init);

    return nativeFetch(input, init).then(async (res) => {
      if (!res.ok) return res;

      let json = null;
      try {
        json = await res.clone().json();
      } catch (e) {
        return res; // not JSON (a plain form post): nothing to hand back, the page renders the cart itself
      }

      const wanted = json && json.sections ? Object.keys(json.sections) : [];
      try {
        const result = await lock(() => sync(wanted));
        if (!result) return res; // no gift change: the theme's reply is already correct

        if (wanted.length) {
          // Swap in sections rendered after the gift change, so the theme draws the final cart once.
          json.sections = Object.assign({}, json.sections, result.sections);
          if (typeof json.item_count === 'number') {
            const fresh = await request('cart.js');
            Object.assign(json, fresh, { sections: json.sections });
          }
        } else {
          paint(result.sections); // the caller renders nothing from this reply (an app), so draw it here
        }
      } catch (e) {
        return res;
      }

      return new Response(JSON.stringify(json), {
        status: res.status,
        statusText: res.statusText,
        headers: { 'Content-Type': 'application/json' },
      });
    });
  };

  /* ---------- path 2: apps that use XMLHttpRequest, page loads, back button ---------- */

  const parse = (html, selector) => {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  };

  function paint(sections) {
    DRAWER_SECTIONS.forEach((id) => {
      const host = document.getElementById(id);
      const html = sections[id] ? parse(sections[id], '.shopify-section') : null;
      if (host && html !== null) host.innerHTML = html;
    });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'patio-gifts' } }));
  }

  let timer = null;
  function background() {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      lock(() => sync(DRAWER_SECTIONS))
        .then((result) => {
          if (result) paint(result.sections);
        })
        .catch(() => {});
    }, 250);
  }

  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isCartWrite(url)) this.addEventListener('loadend', background);
    return nativeOpen.apply(this, arguments);
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) background();
  });

  /* ---------- checkout waits for any gift change in flight ---------- */

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest('[name="checkout"], a[href*="/checkout"]');
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      clearTimeout(timer);
      timer = null;
      lock(() => sync([]))
        .catch(() => {})
        .then(() => {
          const form = button.form || button.closest('form');
          if (button.tagName === 'A') window.location.href = button.href;
          else if (form) {
            const flag = document.createElement('input');
            flag.type = 'hidden';
            flag.name = 'checkout';
            flag.value = '1';
            form.appendChild(flag);
            form.submit();
          } else window.location.href = `${root}checkout`;
        });
    },
    true
  );

  window.PatioCartGifts = { sync: () => lock(() => sync(DRAWER_SECTIONS)).then((r) => r && paint(r.sections)) };

  // Page load: fix up a cart that changed elsewhere (another tab, a discount link). Paints only if something changed.
  background();
})();
